/**
 * Reports client-side failures onto the active speed job's diagnostic trail.
 *
 * When someone says "job X is stuck", the server's own error trail explains what the backend hit —
 * but a whole class of failure never reaches the backend at all: a rejected wallet signature, a
 * network drop mid-request, an unhandled rejection, a render crash. Those are exactly the ones that
 * leave a job looking frozen for no visible reason.
 *
 * Everything sent here is untrusted and the server treats it that way: it stamps the timestamp
 * itself, forces `source: 'client'`, redacts the content, and nothing in the saga reads it.
 *
 * Deliberately best-effort. A reporting failure must never surface to the user or replace the
 * original error — if this cannot get through, the thing being reported is still the real problem.
 */
import type { KyInstance } from 'ky';

/** Set while an operation is in flight, so reports land on the right job. */
let activeJobId: string | null = null;
let api: KyInstance | null = null;

/**
 * Bound so a runaway loop cannot hammer the endpoint. The SERVER is deliberately not rate limited
 * (a burst of failures is the diagnostic worth having, and it caps the trail at 100 instead), so
 * the restraint belongs here: identical repeated messages are pointless, not informative.
 */
const recentlySent = new Map<string, number>();
const DUPLICATE_WINDOW_MS = 10_000;

export function setActiveJobForErrorReports(
    jobId: string | null,
    authedApi: KyInstance | null,
): void {
    activeJobId = jobId;
    api = authedApi;
    if (jobId === null) {
        recentlySent.clear();
    }
}

export function getActiveJobForErrorReports(): string | null {
    return activeJobId;
}

/** Reduce anything throwable to a short string without leaking an object dump. */
function describe(error: unknown): string {
    if (error === null || error === undefined) return 'unknown error';
    if (typeof error === 'string') return error;
    const anyError = error as any;
    return String(anyError?.message ?? anyError?.reason ?? anyError?.name ?? String(error));
}

export function reportJobError(input: {
    message: string;
    code?: string | null;
    detail?: Record<string, unknown>;
}): void {
    const jobId = activeJobId;
    const client = api;
    if (!jobId || !client) return;

    const key = `${input.code ?? ''}|${input.message}`;
    const now = Date.now();
    const last = recentlySent.get(key);
    if (last !== undefined && now - last < DUPLICATE_WINDOW_MS) return;
    recentlySent.set(key, now);

    void client
        .post(`speed/job/${encodeURIComponent(jobId)}/feedback`, {
            json: {
                message: input.message,
                code: input.code ?? null,
                detail: input.detail ?? null,
            },
        })
        .catch(() => {
            // swallowed on purpose: see the file header. Reporting is never the user's problem.
        });
}

/** Convenience for a caught throwable. */
export function reportJobException(
    error: unknown,
    context: { where: string; detail?: Record<string, unknown> },
): void {
    reportJobError({
        message: describe(error),
        code: (error as any)?.code ?? null,
        detail: { where: context.where, ...(context.detail ?? {}) },
    });
}

let globalsInstalled = false;

/**
 * Catches the failures nothing else does: unhandled promise rejections and uncaught errors.
 * Installed once; only reports while a job is actually active, so ordinary background noise on
 * other pages is not attributed to an operation.
 */
export function installGlobalJobErrorReporting(): void {
    if (globalsInstalled || typeof window === 'undefined') return;
    globalsInstalled = true;

    window.addEventListener('unhandledrejection', (event) => {
        reportJobException(event.reason, { where: 'unhandledrejection' });
    });
    window.addEventListener('error', (event) => {
        reportJobException(event.error ?? event.message, {
            where: 'window.onerror',
            detail: {
                source: (event as ErrorEvent).filename ?? null,
                line: (event as ErrorEvent).lineno ?? null,
            },
        });
    });
}
